import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthState from '@/states/auth.state';
import { env } from '@/utils/constants/env';

export const VerifyEmail: React.FC = () => {
  const navigate = useNavigate();
  const { userProfile, token, logout, loadUserProfile } = useAuthState();
  const [isSending, setIsSending] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-detect verification: when the user returns to this tab (e.g. after clicking the
  // verification link in another tab/email app), re-check status and enter the app without
  // needing to press the button. /verify-email has no route guard, so we do it here.
  useEffect(() => {
    const recheck = async () => {
      if (document.visibilityState === 'hidden') return;
      await loadUserProfile(token ?? undefined, true);
      if (useAuthState.getState().userProfile?.email_verified_at) {
        navigate('/workspaces');
      }
    };

    window.addEventListener('focus', recheck);
    document.addEventListener('visibilitychange', recheck);
    return () => {
      window.removeEventListener('focus', recheck);
      document.removeEventListener('visibilitychange', recheck);
    };
  }, [token, loadUserProfile, navigate]);

  const handleResend = async () => {
    setIsSending(true);
    setError(null);
    try {
      const response = await fetch(`${env.apiBaseUrl}/api/email/verification-notification`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Gagal mengirim ulang email verifikasi.');
      }

      setIsSent(true);
    } catch (err) {
      console.error('VerifyEmail: Gagal mengirim ulang verifikasi email', err);
      setError('Gagal mengirim ulang email. Silakan coba lagi.');
    } finally {
      setIsSending(false);
    }
  };

  const handleRefresh = async () => {
    setIsChecking(true);
    setError(null);
    try {
      // Force a fresh /me fetch (bypasses the 5-minute profile cache) so a just-verified
      // email is reflected instead of relying on the persisted, possibly-stale profile.
      await loadUserProfile(token ?? undefined, true);

      if (useAuthState.getState().userProfile?.email_verified_at) {
        // Verified — leave the guardless /verify-email page and enter the app.
        // The workspaces route (ProtectedGlobalRoute) re-checks and lets a verified user through.
        navigate('/workspaces');
        return;
      }

      setError('Email belum terverifikasi. Pastikan Anda sudah mengklik tautan verifikasi di email Anda.');
    } catch (err) {
      console.error('VerifyEmail: Gagal memeriksa status verifikasi', err);
      setError('Gagal memeriksa status verifikasi. Silakan coba lagi.');
    } finally {
      setIsChecking(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="bg-white rounded-2xl shadow-lg p-10 max-w-md w-full text-center">
        {/* Icon */}
        <div className="flex items-center justify-center w-20 h-20 mx-auto mb-6 bg-yellow-50 rounded-full">
          <svg
            className="w-10 h-10 text-yellow-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Verifikasi Email Anda</h1>
        <p className="text-gray-500 mb-1">Kami telah mengirimkan tautan verifikasi ke:</p>
        <p className="font-semibold text-gray-800 mb-6">{userProfile?.email ?? '-'}</p>

        <p className="text-sm text-gray-500 mb-8">
          Buka email Anda dan klik tautan verifikasi untuk melanjutkan. Jika tidak menemukan email, periksa folder{' '}
          <span className="font-medium">spam</span> atau kirim ulang di bawah ini.
        </p>

        {/* Success message */}
        {isSent && (
          <div className="mb-4 px-4 py-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
            Email verifikasi berhasil dikirim ulang. Silakan cek kotak masuk Anda.
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
        )}

        {/* Actions */}
        <div className="flex flex-col gap-3">
          <button
            id="btn-refresh-verification"
            onClick={handleRefresh}
            disabled={isChecking}
            className="w-full py-3 px-4 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-xl transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isChecking ? 'Memeriksa...' : 'Sudah Verifikasi? Klik di sini'}
          </button>

          <button
            id="btn-resend-verification"
            onClick={handleResend}
            disabled={isSending || isSent}
            className="w-full py-3 px-4 border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium rounded-xl transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSending ? 'Mengirim...' : 'Kirim Ulang Email Verifikasi'}
          </button>

          <button
            id="btn-logout-from-verify"
            onClick={logout}
            className="text-sm text-gray-400 hover:text-gray-600 transition-colors duration-200 mt-1"
          >
            Keluar dari akun ini
          </button>
        </div>
      </div>
    </div>
  );
};
